import { supabase } from '@/lib/supabase/client';

// Types pour les données Analytics
export interface AnalyticsMetrics {
  totalVisitors: number;
  totalPageViews: number;
  bounceRate: number;
  avgSessionDuration: number;
  trends: {
    visitors: number;
    pageViews: number;
    bounceRate: number;
    sessionDuration: number;
  };
}

export interface TopPage {
  path: string;
  title: string;
  views: number;
  uniqueViews: number;
}

export interface TrafficSource {
  source: string;
  medium: string;
  sessions: number;
  percentage: number;
}

export interface DeviceStats {
  deviceCategory: string;
  sessions: number;
  percentage: number;
}

export interface VisitorData {
  date: string;
  visitors: number;
}

// Types pour les périodes
export type DateRange = '7daysAgo' | '30daysAgo' | '90daysAgo';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Appelle une Edge Function Supabase avec authentification
 */
async function callEdgeFunction<T>(
  functionName: string,
  params: Record<string, string | number>
): Promise<T> {
  // Récupérer le token de session actuel
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Non authentifié');
  }

  // Construire l'URL avec les paramètres
  const url = new URL(`${SUPABASE_URL}/functions/v1/${functionName}`);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, String(value));
  });

  // Appeler l'Edge Function
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Erreur lors de la requête Analytics');
  }

  return response.json();
}

/**
 * Récupère les métriques principales pour une période donnée
 */
export async function getAnalyticsMetrics(
  startDate: DateRange = '30daysAgo',
  endDate: string = 'today'
): Promise<AnalyticsMetrics> {
  return callEdgeFunction<AnalyticsMetrics>('analytics-metrics', {
    startDate,
    endDate,
  });
}

/**
 * Récupère les pages les plus vues
 */
export async function getTopPages(
  startDate: DateRange = '30daysAgo',
  endDate: string = 'today',
  limit: number = 10
): Promise<TopPage[]> {
  return callEdgeFunction<TopPage[]>('analytics-top-pages', {
    startDate,
    endDate,
    limit,
  });
}

/**
 * Récupère les sources de trafic
 */
export async function getTrafficSources(
  startDate: DateRange = '30daysAgo',
  endDate: string = 'today'
): Promise<TrafficSource[]> {
  return callEdgeFunction<TrafficSource[]>('analytics-traffic-sources', {
    startDate,
    endDate,
  });
}

/**
 * Récupère les statistiques par device
 */
export async function getDeviceStats(
  startDate: DateRange = '30daysAgo',
  endDate: string = 'today'
): Promise<DeviceStats[]> {
  return callEdgeFunction<DeviceStats[]>('analytics-device-stats', {
    startDate,
    endDate,
  });
}

/**
 * Récupère l'évolution des visiteurs jour par jour
 */
export async function getVisitorsByDay(
  startDate: DateRange = '30daysAgo',
  endDate: string = 'today'
): Promise<VisitorData[]> {
  return callEdgeFunction<VisitorData[]>('analytics-visitors-by-day', {
    startDate,
    endDate,
  });
}
